import { useStaticQuery, graphql } from 'gatsby';

import type { IContentCarouselProps } from '@klaviyo-mkt/gatsby-components/organisms/content-carousel/types';
import type { IFullColumn } from '@klaviyo-mkt/gatsby-components/organisms/full-column/types';
import type { IGatsbyImageData } from 'gatsby-plugin-image';
import type { ILogosProps } from '@klaviyo-mkt/gatsby-components/organisms/logos/types';
import type { IPricing } from '@klaviyo-mkt/gatsby-components/organisms/pricing/types';
import type { IVideoMediaProps } from '@klaviyo-mkt/gatsby-components/organisms/video/types';
import type { TBackgroundColor } from '@klaviyo-mkt/gatsby-components/utils/wrapper/types';
import type { ICustomPadding } from '@klaviyo-mkt/gatsby-components/atoms/custom-padding/types';

interface IAffiliatePageTemplateData {
  anchor?: {
    jumpAnchor?: {
      id?: string;
    };
  };
  backgroundColor: TBackgroundColor;
  customPadding: ICustomPadding;
  badges: {
    logosOrganism: ILogosProps;
  };
  bodyCopy: string;
  customers: { 
    contentCarousel: IContentCarouselProps;
  };
  customerLogos: {
    logosOrganism: ILogosProps;
  };
  demoVideo: {
    video: IVideoMediaProps;
  };
  formCopy: string;
  formHeadline: string;
  fullColumn: {
    fullColumn: IFullColumn;
  };
  headline: string;
  image?: {
    altText?: string;
    gatsbyImage?: IGatsbyImageData;
  };
  pricing: {
    pricing: IPricing;
  };
  resources: { 
    contentCarousel: IContentCarouselProps;
  };
  navigationColor: string;
  shortTitle: string;
}

export const useAffiliatePageTemplateQuery = () => {
  const data = useStaticQuery(graphql`
    query AffiliatePageTemplateQuery {
      wp {
        affiliatePageTemplate {
          affiliatePageTemplate {
            anchor {
              jumpAnchor {
                id
              }
            }
            bodyCopy
            headline,
            image {
              altText
              gatsbyImage: wpGatsbyImage(
                placeholder: BLURRED
                width: 1366
              )
            }
            formCopy
            formHeadline
            
            badges {
              logosOrganism {
                background
                buttons {
                  leftIcon {
                    iconName
                  }
                  openInNewTab
                  rightIcon {
                    iconName
                  }
                  text
                  url
                }
                headline
                logos {
                  image {
                    altText
                    gatsbyImage: wpGatsbyImage(
                      placeholder: BLURRED
                      width: 196
                    )
                  }
                  url
                }
              }
            }
            customers {
              contentCarousel {
                alignment
                backgroundColor
                carouselBehavior
                headerFont
                headline
                subheadline
                showFeatureCard
                featureCard {
                  bfcmFeatureCard {
                    backgroundColor
                    fontType
                    eyebrow
                    headline
                    subheadline
                    contentCard {
                      backgroundColor
                      badge {
                        color
                        text
                      }
                      buttons {
                        text
                        url
                      }
                      eyebrow
                      headline
                      subheadline
                      textColor
                    }
                    contentImage {
                      videoUrl
                      mobileImage {
                        altText
                        gatsbyImage: wpGatsbyImage(
                          placeholder: BLURRED
                          width: 1366
                        )
                        altText
                      }
                      desktopImage {
                        altText
                        gatsbyImage: wpGatsbyImage(
                          placeholder: BLURRED
                          width: 1366
                        )
                        altText
                      }
                    }
                    customPadding {
                      customPaddingBottom
                      customPaddingTop
                    }
                  }
                }
                mediaCards {
                  mediaCard {
                    mediaType
                    icon {
                      iconName
                    }
                    image {
                      altText
                      mediaDetails {
                        height
                        width
                      }
                      sizes
                      sourceUrl
                      srcSet
                      gatsbyImage: wpGatsbyImage(
                        placeholder: BLURRED
                        width: 588
                      )
                    }
                    videoDuration
                    videoUrl
                    videoPoster {
                      altText
                      gatsbyImage: wpGatsbyImage(
                        placeholder: BLURRED
                        width: 588
                      )
                    }
                    title
                    bodyCopy
                    buttons {
                      leftIcon {
                        iconName
                      }
                      openInNewTab
                      rightIcon {
                        iconName
                      }
                      text
                      url
                    }
                    badges {
                      badge
                    }
                  }
                }
                buttons {
                  leftIcon {
                    iconName
                  }
                  openInNewTab
                  rightIcon {
                    iconName
                  }
                  text
                  url
                }
                narrowColumns
              }
            }
            customerLogos {
              logosOrganism {
                background
                buttons {
                  leftIcon {
                    iconName
                  }
                  openInNewTab
                  rightIcon {
                    iconName
                  }
                  text
                  url
                }
                headline
                logos {
                  image {
                    altText
                    gatsbyImage: wpGatsbyImage(
                      placeholder: BLURRED
                      width: 196
                    )
                  }
                  url
                }
              }
            }
            demoVideo {
              video {
                backgroundColor
                poster {
                  altText
                  gatsbyImage: wpGatsbyImage(
                    formats: WEBP
                    placeholder: BLURRED
                    width: 1366
                  )
                  localFile {
                    publicURL
                  }
                }
                preRollUrl
                videoDuration
                videoPlayButtonColor
                videoTitle
                videoOverlayTitle
                videoUrl
              }
            }
            fullColumn {
              fullColumn {
                backgroundColor
                buttons {
                  leftIcon {
                    iconName
                  }
                  openInNewTab
                  rightIcon {
                    iconName
                  }
                  text
                  url
                }
                customPadding {
                  customPaddingBottom
                  customPaddingTop  
                }
                eyebrow
                headline
                headlineFont {
                  fontFamily
                }
                image {
                  altText
                  gatsbyImage: wpGatsbyImage(
                    placeholder: BLURRED
                    width: 1366
                  )
                }
                mobileImage {
                  altText
                  gatsbyImage(
                    placeholder: BLURRED
                    width: 768
                  )
                }
              headline
              image {
                altText
                gatsbyImage: wpGatsbyImage(
                  placeholder: BLURRED
                  width: 1366
                )
              }
              paragraph
              variant
              }
            }
            navigationColor
            shortTitle
            pricing {
              pricing {
              excludeFree
              cardContent {
                emailAndSms {
                  buttons {
                    leftIcon {
                      iconName
                    }
                    openInNewTab
                    rightIcon {
                      iconName
                    }
                    text
                    url
                  }
                }
                email {
                  buttons {
                    leftIcon {
                      iconName
                    }
                    openInNewTab
                    rightIcon {
                      iconName
                    }
                    text
                    url
                  }
                }
                free {
                  buttons {
                    leftIcon {
                      iconName
                    }
                    openInNewTab
                    rightIcon {
                      iconName
                    }
                    text
                    url
                  }
                }
              }
              comparisonChart {
                backgroundColor
                buttons {
                  leftIcon {
                    iconName
                  }
                  openInNewTab
                  rightIcon {
                    iconName
                  }
                  text
                  url
                }
                chart {
                  ... on WpComparisonChart {
                    content
                  }
                }
                description
                disclaimer
                headline
                showFlag
              }
            }
            }
            resources {
              contentCarousel {
                alignment
                backgroundColor
                carouselBehavior
                headerFont
                headline
                subheadline
                showFeatureCard
                featureCard {
                  bfcmFeatureCard {
                    backgroundColor
                    fontType
                    eyebrow
                    headline
                    subheadline
                    contentCard {
                      backgroundColor
                      badge {
                        color
                        text
                      }
                      buttons {
                        text
                        url
                      }
                      eyebrow
                      headline
                      subheadline
                      textColor
                    }
                    contentImage {
                      videoUrl
                      mobileImage {
                        altText
                        gatsbyImage: wpGatsbyImage(
                          placeholder: BLURRED
                          width: 1366
                        )
                        altText
                      }
                      desktopImage {
                        altText
                        gatsbyImage: wpGatsbyImage(
                          placeholder: BLURRED
                          width: 1366
                        )
                        altText
                      }
                    }
                    customPadding {
                      customPaddingBottom
                      customPaddingTop
                    }
                  }
                }
                mediaCards {
                  mediaCard {
                    mediaType
                    icon {
                      iconName
                    }
                    image {
                      altText
                      mediaDetails {
                        height
                        width
                      }
                      sizes
                      sourceUrl
                      srcSet
                      gatsbyImage: wpGatsbyImage(
                        placeholder: BLURRED
                        width: 588
                      )
                    }
                    videoDuration
                    videoUrl
                    videoPoster {
                      altText
                      gatsbyImage: wpGatsbyImage(
                        placeholder: BLURRED
                        width: 588
                      )
                    }
                    title
                    bodyCopy
                    buttons {
                      leftIcon {
                        iconName
                      }
                      openInNewTab
                      rightIcon {
                        iconName
                      }
                      text
                      url
                    }
                    badges {
                      badge
                    }
                  }
                }
                buttons {
                  leftIcon {
                    iconName
                  }
                  openInNewTab
                  rightIcon {
                    iconName
                  }
                  text
                  url
                }
                narrowColumns
              }
            }
          }
        }
      }
    }
  `);

  const affiliateTemplateData: IAffiliatePageTemplateData = data?.wp?.affiliatePageTemplate?.affiliatePageTemplate || {};

  return affiliateTemplateData;
};
